import { useDispatch, useSelector } from "react-redux";
import { FC, useState } from "react";
import * as _ from "lodash";
import { Formik, useFormikContext } from "formik";
import { useDeepCompareEffect } from "use-deep-compare";

import { Optional } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import {
  Button,
  Typography,
  Space,
  Input,
  AntDesignInput,
  Icon,
  Barn,
  Select,
  Modal,
  CopyOutlined,
  Alert,
  List,
  Form,
  InfoCircleFilled
} from "@ctra/components";

import {
  Enterprise,
  EnterpriseAppState,
  FarmEntity,
  MetricCollection,
  MetricEntity,
  PageResponse,
  SavedCards
} from "@ctra/api";

import { InternalisedCard } from "@ctra/api/src/enterprise/entities";

import { useFarm, useFarmList } from "@farms";
import { GACategories, useSavedCharts } from "@analytics";
import { useDataDictionary } from "@base";

import { commonModalProps } from "../DashboardPage";
import styles from "./CopyDashboardModal.module.less";

const { Group } = AntDesignInput;
const { Text } = Typography;

interface CopyDashboardModalProps {
  visible: boolean;
  group: Optional<string>;
  onCancel: () => void;
}

interface FormValues {
  label: string;
  farmID: FarmEntity["id"] | "main";
  tabs: Array<PageResponse>;
}

const {
  analytics: {
    v3: {
      confirm: {
        newTab,
        copyTab,
        error: { duplicateTab }
      }
    },
    config: {
      metric: { displayName }
    }
  },
  navigation,
  timeline: {
    createEvent: { submit }
  }
} = Content;

/**
 * Value to represent all farms
 * @type {number}
 */
const allFarmsValue = "main";

/**
 * Form business logic
 * @param {string | undefined} group
 * @returns {JSX.Element}
 * @constructor
 */
const FormComponent: FC<Pick<CopyDashboardModalProps, "group">> = ({ group }) => {
  const { t } = useTranslation();
  const { farm } = useFarm();
  const { farmList } = useFarmList();
  const { savedCharts } = useSavedCharts();

  const {
    api: { resolveMetricByVariantID, getChartInfo }
  } = useDataDictionary();

  const {
    setFieldValue,
    values: { label, farmID }
  } = useFormikContext<FormValues>();

  const [unsupportedVariants, setUnsupportedVariants] = useState<Optional<MetricEntity["variants"]>>();

  /**
   * Attempt to get metrics for the copy farm
   */
  const metrics = useSelector<EnterpriseAppState, MetricCollection>((state) =>
    Enterprise.entities.getMetricList(state, {
      farmID: farmID === allFarmsValue || !farmID ? void 0 : farmID
    })
  );

  /**
   * Get the current charts for the selected tab
   * @type {SavedCardsPayload | undefined}
   */
  const chartsOfCurrentTab = _.find(savedCharts, { id: group })?.cards;

  /**
   * Set the upsupported variants upon select
   */
  useDeepCompareEffect(() => {
    const supportedVariants = _.flatMap(metrics, ({ variants }) => _.map(variants));
    const currentVariants = _.compact(_.map(chartsOfCurrentTab, "variantId"));

    setUnsupportedVariants(_.difference(currentVariants, supportedVariants));
  }, [farmID, group, chartsOfCurrentTab]);

  /**
   * Options for the farm select
   * @type {({label: string, value: null} | {label: string | null | undefined, value: number})[]}
   */
  const farmOptions = _.compact([
    _.size(farmList) > 1
      ? {
          label: t<string>(navigation.main.allFarms),
          value: allFarmsValue
        }
      : null,
    ..._.map(farm && farmList, ({ name, id }) => ({
      label: name,
      value: id
    }))
  ]);

  /**
   * Attempt to get the saved charts for copy farm
   */
  const targetFarmLayout = useSelector<EnterpriseAppState, Array<PageResponse>>((state) =>
    Enterprise.entities.getServerSpecLayoutOfFarm(state, {
      farmID: !!farmID ? farmID.toString() : allFarmsValue
    })
  );

  /**
   * Make a chart list for the new tab from the ones which are supported by the farm
   * @type {InternalisedCard[]}
   */
  const updatedCharts: InternalisedCard[] = _.reject(
    chartsOfCurrentTab,
    ({ variantIDs }) => _.size(_.intersection(unsupportedVariants, variantIDs)) > 0
  );

  useDeepCompareEffect(() => {
    /**
     * Make a card response list from the updated charts
     * @type {CardResponse[]}
     */
    const cards = _.map(updatedCharts, (chart) => {
      return Enterprise.entities.toCardResponse(chart);
    });

    /**
     * Compose the updated tabs
     * @type {(PageResponse[]}
     */
    const updatedTabs: (PageResponse | Omit<PageResponse, "id">)[] = [
      ...targetFarmLayout,
      {
        name: label,
        cards
      }
    ];

    setFieldValue("tabs", updatedTabs);
  }, [targetFarmLayout, label, updatedCharts]);

  /**
   * Alert to be shown if there are unsupported metrics
   */
  const nonSupportedAlert = unsupportedVariants?.length ? (
    <Alert
      className={styles.Alert}
      type="warning"
      message={
        <>
          <Space>
            <InfoCircleFilled />
            {t(copyTab.alert)}
          </Space>
          <List
            bordered={false}
            dataSource={unsupportedVariants}
            renderItem={(variant) => {
              const metricEntity = resolveMetricByVariantID(variant);
              const chartInfo = metricEntity && getChartInfo(metricEntity.id, variant);

              return (
                <List.Item className={styles.List}>
                  {metricEntity && chartInfo && (
                    <Text>- {t(displayName(chartInfo.nameToDisplay), { makeDefaultValue: true })}</Text>
                  )}
                </List.Item>
              );
            }}
          />
        </>
      }
    />
  ) : null;

  return (
    <Form layout="vertical">
      <Form.Item name="label" label={t<string>(copyTab.dashboard)}>
        <Input name="label" placeholder={t<string>(newTab.inputPlaceholder)} />
      </Form.Item>
      <Form.Item name="farmID" label={t<string>(copyTab.farm)}>
        <Group compact className={styles.InputGroup}>
          <Button size="middle" className={styles.Icon} icon={<Icon component={Barn} />} disabled />
          <Select
            name="farmID"
            showSearch
            placeholder={t<string>(copyTab.farmPlaceholder)}
            className={styles.Select}
            disabled={_.isEmpty(farmList)}
            showArrow={true}
            size="middle"
            filterOption={(input, option) =>
              !!option && _.toLower(_.toString(option.label)).includes(_.toLower(input))
            }
            defaultValue={farm?.id}
            options={farmOptions}
          />
        </Group>
      </Form.Item>
      {nonSupportedAlert}
    </Form>
  );
};
/**
 * Modal to copy tab from one farm to another
 * @param param0
 * @returns
 */
export const CopyDashboardModal: FC<CopyDashboardModalProps> = ({ visible, group, onCancel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { savedCharts } = useSavedCharts();
  const { farm } = useFarm();

  /**
   * Label of current tab
   * @type {string | undefined}
   */
  const currentTabLabel: string | undefined = _.find(savedCharts, {
    id: group
  })?.name;

  /**
   * New default value
   * @type {string}
   */
  const defaultLabel = `${currentTabLabel}-copy`;

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={{
        label: defaultLabel,
        farmID: farm?.id || allFarmsValue,
        tabs: []
      }}
      validate={({ tabs }) => {
        const tabNames = _.map(tabs, "groupName");

        if (_.size(tabNames) !== _.size(_.uniq(tabNames))) {
          return {
            label: t<string>(duplicateTab)
          };
        }
      }}
      onSubmit={({ farmID, tabs }, { setSubmitting }) => {
        setSubmitting(true);
        dispatch(SavedCards.actions.updateTabs.start(farmID.toString(), tabs));
        onCancel();
      }}
    >
      {({ values: { farmID }, submitForm, isSubmitting, errors }) => (
        <Modal
          {...commonModalProps}
          open={visible}
          onCancel={onCancel}
          className={styles.Modal}
          okText={t(submit)}
          onOk={submitForm}
          okButtonProps={{
            ...commonModalProps.okButtonProps,
            disabled: !farmID || isSubmitting || _.size(errors) > 0
          }}
          data-gtm-category={GACategories.dashboards}
          data-gtm-action="Copy dashboard"
          data-gtm-info={JSON.stringify({ dashboard: currentTabLabel })}
          title={
            <Space>
              <CopyOutlined />
              {t(copyTab.title)}
            </Space>
          }
        >
          <FormComponent group={group} />
        </Modal>
      )}
    </Formik>
  );
};
